import React, { useState, useMemo, useRef, useEffect } from 'react';
import './BulkResults.css';

const PAGE_SIZES = [25, 50, 100, 'All'];

// ── Config maps ───────────────────────────────────────────────────────────

const REC_CFG = {
  HIGHLY_RECOMMENDED: { label: 'Highly Recommended', color: 'var(--rec-highly-recommended)', short: 'HIGHLY REC' },
  RECOMMENDED:        { label: 'Recommended',         color: 'var(--rec-recommended)',        short: 'REC' },
  REVIEW:             { label: 'Review',               color: 'var(--rec-review)',             short: 'REVIEW' },
  DO_NOT_SEND:        { label: 'Do Not Send',          color: 'var(--rec-donotsend)',          short: 'DO NOT SEND' },
};

const RISK_CFG = {
  LOW:     { color: 'var(--risk-low)',    label: 'Low' },
  MEDIUM:  { color: 'var(--risk-medium)', label: 'Med' },
  HIGH:    { color: 'var(--risk-high)',   label: 'High' },
  UNKNOWN: { color: 'var(--text-dim)',    label: '?' },
};

const LEVEL_CFG = {
  high:   { color: 'var(--high)',   label: 'High' },
  good:   { color: 'var(--good)',   label: 'Good' },
  review: { color: 'var(--review)', label: 'Review' },
  low:    { color: 'var(--low)',    label: 'Low' },
};

// ── Sub-components ────────────────────────────────────────────────────────

function RecBadge({ rec }) {
  const cfg = REC_CFG[rec] || { label: rec || '—', color: 'var(--text-dim)', short: rec || '?' };
  return (
    <span className="rec-badge" style={{ '--badge-color': cfg.color }}>
      {cfg.short}
    </span>
  );
}

function RiskDot({ risk }) {
  const cfg = RISK_CFG[risk] || RISK_CFG.UNKNOWN;
  return (
    <span className="risk-dot" style={{ color: cfg.color }} title={`Spam risk: ${cfg.label}`}>
      {cfg.label}
    </span>
  );
}

function ScoreBar({ score, level }) {
  const cfg = LEVEL_CFG[level] || LEVEL_CFG.review;
  return (
    <div className="score-bar-wrap" title={`Score: ${score ?? '—'} (${cfg.label})`}>
      <span className="score-bar-num" style={{ color: cfg.color }}>{score ?? '—'}</span>
      <div className="score-bar-track">
        <div
          className="score-bar-fill"
          style={{ width: `${score ?? 0}%`, background: cfg.color }}
        />
      </div>
    </div>
  );
}

function CheckDot({ passed, warn, skipped, title }) {
  if (skipped) return <span className="cdot cdot-skip" title={title || 'Skipped'}>–</span>;
  if (warn)    return <span className="cdot cdot-warn" title={title || 'Warning'}>⚠</span>;
  if (passed)  return <span className="cdot cdot-pass" title={title || 'Pass'}>✓</span>;
  return         <span className="cdot cdot-fail" title={title || 'Fail'}>✗</span>;
}

function DetailPanel({ r }) {
  const c = r.checks || {};
  const dns = c.dns || {};
  const smtp = c.smtp || {};
  const disp = c.disposable || {};

  return (
    <div className="detail-panel">
      {/* Recommendation reason */}
      {r.recommendationReason && (
        <div className="detail-section">
          <div className="detail-section-title">Recommendation</div>
          <div className="detail-reason">{r.recommendationReason}</div>
        </div>
      )}

      {/* Spam risk */}
      {r.spamRiskReason && (
        <div className="detail-section">
          <div className="detail-section-title">Spam Risk</div>
          <div className="detail-reason">{r.spamRiskReason}</div>
        </div>
      )}

      {/* DNS details */}
      <div className="detail-section">
        <div className="detail-section-title">DNS / Infrastructure</div>
        <div className="detail-grid">
          <div className="detail-kv">
            <span className="detail-k">MX Records</span>
            <span className="detail-v">
              {dns.mxRecords?.length ? dns.mxRecords.slice(0, 3).join(', ') : '—'}
            </span>
          </div>
          <div className="detail-kv">
            <span className="detail-k">SPF Record</span>
            <span className="detail-v detail-v--mono">
              {dns.spfRecord ? dns.spfRecord.slice(0, 60) + (dns.spfRecord.length > 60 ? '…' : '') : '—'}
            </span>
          </div>
          <div className="detail-kv">
            <span className="detail-k">DMARC Record</span>
            <span className="detail-v detail-v--mono">
              {dns.dmarcRecord ? dns.dmarcRecord.slice(0, 60) + (dns.dmarcRecord.length > 60 ? '…' : '') : '—'}
            </span>
          </div>
        </div>
      </div>

      {/* SMTP details */}
      <div className="detail-section">
        <div className="detail-section-title">SMTP Probe</div>
        <div className="detail-grid">
          <div className="detail-kv">
            <span className="detail-k">SMTP State</span>
            <span className="detail-v">{r.smtpState || '—'}</span>
          </div>
          <div className="detail-kv">
            <span className="detail-k">Catch-All</span>
            <span className="detail-v">{r.catchAllState || '—'}</span>
          </div>
          {smtp.responseCode && (
            <div className="detail-kv">
              <span className="detail-k">Response</span>
              <span className="detail-v detail-v--mono">
                {smtp.responseCode} {smtp.responseMessage}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Disposable / role */}
      {(disp.isDisposable || disp.isRoleBased) && (
        <div className="detail-section">
          <div className="detail-section-title">Address Flags</div>
          <div className="detail-chips">
            {disp.isDisposable && <span className="detail-chip detail-chip--warn">Disposable domain</span>}
            {disp.isRoleBased  && <span className="detail-chip detail-chip--info">Role-based address</span>}
          </div>
        </div>
      )}

      {/* Legacy verdict */}
      {r.verdict && (
        <div className="detail-section">
          <div className="detail-section-title">Verdict</div>
          <div className="detail-reason detail-reason--dim">{r.verdict}</div>
        </div>
      )}
    </div>
  );
}

// ── Export helpers ────────────────────────────────────────────────────────

function buildCSV(rows) {
  const headers = [
    'Email', 'Recommendation', 'Confidence Score', 'Confidence Level',
    'Spam Risk', 'SMTP State', 'Catch-All',
    'Syntax Valid', 'Has MX', 'Has SPF', 'Has DMARC',
    'Is Disposable', 'Is Role-Based',
    'MX Records', 'SPF Record', 'DMARC Record',
    'Recommendation Reason'
  ];
  const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const dataRows = rows.map(r => {
    const c = r.checks || {};
    const dns = c.dns || {};
    const disp = c.disposable || {};
    return [
      r.email,
      r.recommendation ?? '',
      r.confidenceScore ?? '',
      r.confidenceLevel ?? '',
      r.spamRisk ?? '',
      r.smtpState ?? '',
      r.catchAllState ?? '',
      c.syntax?.valid ?? '',
      dns.hasMX ?? '',
      dns.hasSPF ?? '',
      dns.hasDMARC ?? '',
      disp.isDisposable ?? '',
      disp.isRoleBased ?? '',
      (dns.mxRecords || []).map(m => m.exchange || m).join(' | '),
      dns.spfRecord ?? '',
      dns.dmarcRecord ?? '',
      r.recommendationReason ?? '',
    ].map(escape).join(',');
  });
  return [headers.join(','), ...dataRows].join('\n');
}

function downloadCSV(rows, label) {
  const csv = buildCSV(rows);
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `mailverify-${label}-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadJSON(rows, label) {
  const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `mailverify-${label}-${Date.now()}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadTXT(rows, label) {
  const lines = rows.map(r => r.email);
  const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `mailverify-${label}-${Date.now()}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Summary cards ─────────────────────────────────────────────────────────

function SummaryCards({ results, activeFilter, onFilter }) {
  const total      = results.length;
  const highlyRec  = results.filter(r => r.recommendation === 'HIGHLY_RECOMMENDED').length;
  const rec        = results.filter(r => r.recommendation === 'RECOMMENDED').length;
  const review     = results.filter(r => r.recommendation === 'REVIEW').length;
  const doNotSendC = results.filter(r => r.recommendation === 'DO_NOT_SEND').length;

  const scored = results.filter(r => typeof r.confidenceScore === 'number');
  const avgScore = scored.length > 0
    ? Math.round(scored.reduce((s, r) => s + r.confidenceScore, 0) / scored.length)
    : null;

  const cards = [
    { key: 'all',                label: 'Total',            value: total,      color: 'var(--text)',                    sub: avgScore != null ? `avg score ${avgScore}` : null },
    { key: 'HIGHLY_RECOMMENDED', label: 'Highly Rec.',      value: highlyRec,  color: 'var(--rec-highly-recommended)',  sub: total > 0 ? `${Math.round(highlyRec/total*100)}%` : null },
    { key: 'RECOMMENDED',        label: 'Recommended',      value: rec,        color: 'var(--rec-recommended)',         sub: total > 0 ? `${Math.round(rec/total*100)}%` : null },
    { key: 'REVIEW',             label: 'Review',           value: review,     color: 'var(--rec-review)',              sub: total > 0 ? `${Math.round(review/total*100)}%` : null },
    { key: 'DO_NOT_SEND',        label: 'Do Not Send',      value: doNotSendC, color: 'var(--rec-donotsend)',           sub: total > 0 ? `${Math.round(doNotSendC/total*100)}%` : null },
  ];

  return (
    <div className="br-summary-grid">
      {cards.map(card => (
        <button
          key={card.key}
          className={`br-summary-card${activeFilter === card.key ? ' active' : ''}`}
          style={{ '--card-accent': card.color }}
          onClick={() => onFilter(card.key === activeFilter ? 'all' : card.key)}
        >
          <div className="br-sum-value" style={{ color: card.color }}>{card.value}</div>
          <div className="br-sum-label">{card.label}</div>
          {card.sub && <div className="br-sum-sub">{card.sub}</div>}
        </button>
      ))}
    </div>
  );
}

// ── Export dropdown ───────────────────────────────────────────────────────

function ExportMenu({ results, filteredResults }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const safe    = results.filter(r => r.recommendation === 'HIGHLY_RECOMMENDED' || r.recommendation === 'RECOMMENDED');
  const doNotSend = results.filter(r => r.recommendation === 'DO_NOT_SEND');

  return (
    <div className="br-export-wrap" ref={ref}>
      <button className="br-export-btn" onClick={() => setOpen(o => !o)}>
        ↓ Export <span className="br-export-count">{results.length}</span>
      </button>
      {open && (
        <div className="br-export-dropdown">
          <div className="br-export-group">Full export</div>
          <button onClick={() => { downloadCSV(results, 'all'); setOpen(false); }}>📊 CSV — all fields</button>
          <button onClick={() => { downloadJSON(results, 'all'); setOpen(false); }}>{ } JSON — full data</button>
          <div className="br-export-divider" />
          <div className="br-export-group">Filtered lists</div>
          {filteredResults.length !== results.length && (
            <button onClick={() => { downloadCSV(filteredResults, 'filtered'); setOpen(false); }}>
              📄 CSV — current view ({filteredResults.length})
            </button>
          )}
          <button onClick={() => { downloadTXT(safe, 'safe-emails'); setOpen(false); }}>
            ✅ Safe emails only ({safe.length})
          </button>
          <button onClick={() => { downloadTXT(doNotSend, 'do-not-send'); setOpen(false); }}>
            🚫 Do-not-send list ({doNotSend.length})
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────

export default function BulkResults({ results, summary, total, processed, isLive, jobId }) {
  const [filter, setFilter]       = useState('all');
  const [search, setSearch]       = useState('');
  const [sortKey, setSortKey]     = useState('index');
  const [sortDir, setSortDir]     = useState('asc');
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(25);
  const [expanded, setExpanded]   = useState(null);

  // Reset page when filter/search changes
  useEffect(() => { setPage(1); setExpanded(null); }, [filter, search, sortKey, sortDir, pageSize]);

  const filteredResults = useMemo(() => {
    let rows = results.filter(r => {
      const matchFilter = filter === 'all' || r.recommendation === filter;
      const matchSearch = !search || (r.email || '').toLowerCase().includes(search.toLowerCase());
      return matchFilter && matchSearch;
    });

    rows = [...rows].sort((a, b) => {
      let av, bv;
      if (sortKey === 'email') {
        av = a.email || ''; bv = b.email || '';
      } else if (sortKey === 'score') {
        av = a.confidenceScore ?? -1; bv = b.confidenceScore ?? -1;
      } else if (sortKey === 'recommendation') {
        const order = { HIGHLY_RECOMMENDED: 0, RECOMMENDED: 1, REVIEW: 2, DO_NOT_SEND: 3 };
        av = order[a.recommendation] ?? 4; bv = order[b.recommendation] ?? 4;
      } else if (sortKey === 'risk') {
        const order = { LOW: 0, MEDIUM: 1, HIGH: 2, UNKNOWN: 3 };
        av = order[a.spamRisk] ?? 4; bv = order[b.spamRisk] ?? 4;
      } else {
        // index — preserve arrival order
        av = results.indexOf(a); bv = results.indexOf(b);
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return rows;
  }, [results, filter, search, sortKey, sortDir]);

  const isShowAll  = pageSize === 'All';
  const totalPages = isShowAll ? 1 : Math.max(1, Math.ceil(filteredResults.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const paginated  = isShowAll ? filteredResults : filteredResults.slice((safePage - 1) * pageSize, safePage * pageSize);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return <span className="sort-icon">↕</span>;
    return <span className="sort-icon active">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const toggleExpand = (idx) => setExpanded(e => e === idx ? null : idx);

  return (
    <div className="bulk-results">

      {/* Summary cards */}
      <SummaryCards
        results={results}
        activeFilter={filter}
        onFilter={f => setFilter(f)}
      />

      {/* Controls bar */}
      <div className="br-controls">
        <div className="br-controls-left">
          <div className="br-search-wrap">
            <span className="br-search-icon">🔍</span>
            <input
              className="br-search"
              type="search"
              placeholder="Search emails…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="br-search-clear" onClick={() => setSearch('')}>✕</button>
            )}
          </div>
        </div>
        <div className="br-controls-right">
          <select
            className="br-select"
            value={`${sortKey}:${sortDir}`}
            onChange={e => {
              const [k, d] = e.target.value.split(':');
              setSortKey(k); setSortDir(d);
            }}
          >
            <option value="index:asc">Order: Original</option>
            <option value="score:desc">Score: High → Low</option>
            <option value="score:asc">Score: Low → High</option>
            <option value="recommendation:asc">Recommendation</option>
            <option value="risk:asc">Risk: Low → High</option>
            <option value="risk:desc">Risk: High → Low</option>
            <option value="email:asc">Email: A → Z</option>
            <option value="email:desc">Email: Z → A</option>
          </select>
          <select
            className="br-select br-select--sm"
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
          >
            {PAGE_SIZES.map(s => (
              <option key={s} value={s}>{s} / page</option>
            ))}
          </select>
          <ExportMenu results={results} filteredResults={filteredResults} />
        </div>
      </div>

      {/* Live indicator */}
      {isLive && (
        <div className="br-live-bar">
          <span className="br-live-dot" />
          Live — {processed.toLocaleString()} of {total.toLocaleString()} validated…
        </div>
      )}

      {/* Table */}
      <div className="br-table-wrap">
        <table className="br-table">
          <thead>
            <tr>
              <th className="br-th-email" onClick={() => handleSort('email')}>
                Email {sortIcon('email')}
              </th>
              <th onClick={() => handleSort('recommendation')}>
                Rec {sortIcon('recommendation')}
              </th>
              <th onClick={() => handleSort('score')}>
                Score {sortIcon('score')}
              </th>
              <th onClick={() => handleSort('risk')}>
                Risk {sortIcon('risk')}
              </th>
              <th title="Syntax valid">SYN</th>
              <th title="MX records found">MX</th>
              <th title="SPF record found">SPF</th>
              <th title="DMARC record found">DMARC</th>
              <th title="Not disposable">DISP</th>
              <th title="Not role-based">ROLE</th>
              <th title="SMTP probe">SMTP</th>
              <th title="Not catch-all">CATCH</th>
              <th className="br-th-expand" />
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 && (
              <tr>
                <td colSpan={13} className="br-empty">
                  {search || filter !== 'all'
                    ? 'No results match your current filter or search.'
                    : isLive ? 'Waiting for results…' : 'No results to display.'}
                </td>
              </tr>
            )}
            {paginated.map((r, i) => {
              const globalIdx = (safePage - 1) * pageSize + i;
              const c = r.checks || {};
              const dns = c.dns || {};
              const disp = c.disposable || {};
              const smtp = c.smtp || {};
              const noMX = !dns.hasMX;
              const smtpWarn = !smtp.accepted && (smtp.timedOut || smtp.blocked);
              const catchWarn = r.catchAllState === 'YES';
              const catchSkip = r.catchAllState === 'NOT_CHECKED' || r.catchAllState === 'INCONCLUSIVE' || noMX;
              const isExpanded = expanded === globalIdx;

              return (
                <React.Fragment key={r.email + globalIdx}>
                  <tr
                    className={`br-row${isExpanded ? ' expanded' : ''}`}
                    onClick={() => toggleExpand(globalIdx)}
                  >
                    <td className="br-td-email" title={r.email}>{r.email}</td>
                    <td><RecBadge rec={r.recommendation} /></td>
                    <td><ScoreBar score={r.confidenceScore} level={r.confidenceLevel} /></td>
                    <td><RiskDot risk={r.spamRisk} /></td>
                    <td><CheckDot passed={c.syntax?.valid} title="Syntax valid" /></td>
                    <td><CheckDot passed={dns.hasMX} title="MX records" /></td>
                    <td><CheckDot passed={dns.hasSPF} skipped={!dns.hasMX} title="SPF record" /></td>
                    <td><CheckDot passed={dns.hasDMARC} skipped={!dns.hasMX} title="DMARC record" /></td>
                    <td><CheckDot passed={!disp.isDisposable} title="Not disposable" /></td>
                    <td><CheckDot passed={!disp.isRoleBased} warn={disp.isRoleBased} title="Not role-based" /></td>
                    <td><CheckDot
                      passed={r.smtpState === 'ACCEPTED'}
                      warn={smtpWarn}
                      skipped={noMX || r.smtpState === 'NOT_CHECKED'}
                      title={`SMTP: ${r.smtpState || '—'}`}
                    /></td>
                    <td><CheckDot
                      passed={r.catchAllState === 'NO'}
                      warn={catchWarn}
                      skipped={catchSkip}
                      title={`Catch-all: ${r.catchAllState || '—'}`}
                    /></td>

                    <td className="br-td-expand">{isExpanded ? '▲' : '▼'}</td>
                  </tr>
                  {isExpanded && (
                    <tr className="br-detail-row">
                      <td colSpan={13}>
                        <DetailPanel r={r} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="br-pagination">
          <button className="br-page-btn" onClick={() => setPage(1)} disabled={safePage === 1}>«</button>
          <button className="br-page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}>‹</button>
          <span className="br-page-info">
            Page {safePage} of {totalPages}
            <span className="br-page-count"> ({filteredResults.length.toLocaleString()} results)</span>
          </span>
          <button className="br-page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>›</button>
          <button className="br-page-btn" onClick={() => setPage(totalPages)} disabled={safePage === totalPages}>»</button>
        </div>
      )}

      {/* Footer */}
      <div className="br-footer">
        <span>
          Showing {paginated.length.toLocaleString()} of {filteredResults.length.toLocaleString()}
          {filteredResults.length !== results.length && ` (filtered from ${results.length.toLocaleString()})`}
        </span>
        {(filter !== 'all' || search) && (
          <button className="br-clear-btn" onClick={() => { setFilter('all'); setSearch(''); }}>
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}
